/**
 * Campos do form de nova proposta — coordenador (PR-M1-01c-iii.a+.b,
 * reordenado em PR-M1-01c-ii.d).
 *
 * Delega blocos por tipo para subcomponentes especializados:
 *   - `ProposalSaasFields` — quando tipo inclui SaaS (saas, saas_consulting)
 *   - `ProposalConsultingFields` — quando tipo inclui Consulting (consulting, saas_consulting)
 *
 * **Ordem dos blocos** (fluxo mental Karla 2026-04-24):
 *   1. Tipo de proposta (select)
 *   2. Campos estruturais SaaS e/ou Consultoria
 *   3. Checkbox "Bônus SaaS gratuito" (combo)
 *   4. Proposta válida por (dias)
 *   5. **Card "Gerar rascunho com IA"** (`aiAssistantSlot`) — fica aqui
 *      pra IA ter todos os dados estruturais já preenchidos como contexto
 *   6. Notas da proposta (textarea)
 *
 * Extraído do `NewProposalDialog` (mantém pai abaixo do hard-limit).
 */
import type { ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Button, Label, Select } from '@kmgestao/ui';

import {
  PROPOSAL_TYPES,
  PROPOSAL_TYPE_LABELS,
  PROPOSAL_TYPE_DESCRIPTIONS,
  type CreateProposalInput,
  type ProposalType,
} from '../../../features/proposals/index.js';

import { ProposalConsultingFields } from './ProposalConsultingFields.js';
import { ProposalSaasFields } from './ProposalSaasFields.js';

export interface NewProposalFormFieldsProps {
  form: UseFormReturn<CreateProposalInput>;
  disabled: boolean;
  /** Callback quando Karla clica "Expandir" no textarea de notas —
   * pai (NewProposalDialog) abre o NewProposalNotesModal centralizado.
   * Opcional para facilitar testes. */
  onExpandNotes?: () => void;
  /** Quando o tipo de proposta muda via select no topo, o pai precisa
   * reinicializar os defaults dos campos daquela variante (ex: trocar
   * de saas pra consulting exige setar installments_count etc). */
  onProposalTypeChange?: (next: ProposalType) => void;
  /** Slot pra renderizar o card "Gerar rascunho com IA" (NewProposalAiAssistant)
   * **entre "Proposta válida por" e "Notas da proposta"** — garante que a IA
   * veja todos os dados estruturais preenchidos antes de gerar texto. */
  aiAssistantSlot?: ReactNode;
}

const INPUT_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm';

export function NewProposalFormFields({
  form,
  disabled,
  onExpandNotes,
  onProposalTypeChange,
  aiAssistantSlot,
}: NewProposalFormFieldsProps) {
  const { register, watch, formState } = form;
  const proposalType = watch('proposal_type');
  const hasSaas = proposalType === 'saas' || proposalType === 'saas_consulting';
  const hasConsulting = proposalType === 'consulting' || proposalType === 'saas_consulting';

  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="proposal_type">Tipo de proposta</Label>
        <Select
          id="proposal_type"
          {...register('proposal_type')}
          disabled={disabled}
          onChange={(e) => {
            register('proposal_type').onChange(e);
            onProposalTypeChange?.(e.target.value as ProposalType);
          }}
        >
          {PROPOSAL_TYPES.map((t) => (
            <option key={t} value={t}>
              {PROPOSAL_TYPE_LABELS[t]}
            </option>
          ))}
        </Select>
        <p className="text-xs text-muted-foreground">{PROPOSAL_TYPE_DESCRIPTIONS[proposalType]}</p>
      </div>

      {hasSaas ? <ProposalSaasFields form={form} disabled={disabled} /> : null}

      {hasConsulting ? <ProposalConsultingFields form={form} disabled={disabled} /> : null}

      {proposalType === 'saas_consulting' ? (
        <label className="flex cursor-pointer items-start gap-2 rounded-md border border-accent/30 bg-accent/5 p-3 text-sm">
          <input
            type="checkbox"
            {...register('saas_free_during_consulting' as never)}
            disabled={disabled}
            className="mt-0.5 size-4 accent-primary"
          />
          <span>
            <span className="font-medium text-foreground">
              Ativar bônus: SaaS gratuito durante a consultoria.
            </span>{' '}
            Quando marcado, a mensalidade da plataforma SaaS fica suspensa enquanto o contrato de
            consultoria estiver vigente.{' '}
            <strong>
              As parcelas da consultoria são cobradas via Asaas desde o início, normalmente.
            </strong>{' '}
            A mensalidade do SaaS passa a ser cobrada apenas após o término da consultoria, mediante
            confirmação de renovação do cliente.
          </span>
        </label>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="valid_until_days">Proposta válida por (dias)</Label>
        <input
          id="valid_until_days"
          type="number"
          min={1}
          max={90}
          inputMode="numeric"
          {...register('valid_until_days', { valueAsNumber: true })}
          disabled={disabled}
          className={INPUT_CLASS}
        />
      </div>

      {aiAssistantSlot}

      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="notes">Notas da proposta</Label>
          {onExpandNotes ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onExpandNotes}
              disabled={disabled}
              className="h-8 px-2 text-xs"
            >
              Expandir
            </Button>
          ) : null}
        </div>
        <textarea
          id="notes"
          rows={6}
          maxLength={4_000}
          {...register('notes')}
          disabled={disabled}
          placeholder="Texto que vai compor o corpo da proposta. Use a IA acima para gerar automaticamente."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-base leading-relaxed ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm md:leading-6"
        />
        {formState.errors.notes ? (
          <p className="text-xs text-destructive">{formState.errors.notes.message}</p>
        ) : null}
        {onExpandNotes ? (
          <p className="text-xs text-muted-foreground">
            Dica: clique em <span className="font-medium text-foreground">Expandir</span> para ler e
            editar as notas em tela cheia.
          </p>
        ) : null}
      </div>
    </>
  );
}
